import { ActivityDefinition } from '../../types/library';

export type MonitoringTag = {
  /** Chip text, UPPERCASE. */
  label: string;
  /** Filter-dropdown text, Title Case. */
  filterLabel: string;
  color: string;
  tooltip: string;
};

// The end conditions whose repetitions are paced by an on-screen guide instead of being
// counted by the motion algorithm. This is an allowlist on purpose: `EndCondition` does not
// cover every value the library actually ships (`frontal_distance` is real and missing from
// the union), and an unrecognised end condition must fall through to TRACK rather than be
// mistaken for a guided one.
const GUIDED_REP_END_CONDITIONS: ReadonlySet<string> = new Set([
  'guided_rep_non_algo',
  'reps_non_algo',
]);

/* eslint-disable max-len */
export const CAPTURE_TAG: MonitoringTag = {
  label: 'CAPTURE',
  filterLabel: 'Capture',
  color: '#FF9900',
  tooltip:
    'Level 1: Capture Status--This exercise captures and displays the movements performed by patients. However, it does not enable setting or tracking patient goals.',
};

export const GUIDED_REPS_TAG: MonitoringTag = {
  label: 'GUIDED-REPS',
  filterLabel: 'Guided-Reps',
  color: '#C97900',
  tooltip:
    'Guided Reps--The patient is paced through each repetition by an on-screen guide. Repetitions are recorded against the prescribed dosage but are not counted by the motion-tracking algorithm.',
};

export const TRACK_TAG: MonitoringTag = {
  label: 'TRACK',
  filterLabel: 'Track',
  color: '#708541',
  tooltip:
    'Track--The motion-tracking algorithm captures and counts this exercise against the prescribed goals, so progress can be monitored over time.',
};
/* eslint-enable max-len */

export const MONITORING_TAGS: MonitoringTag[] = [CAPTURE_TAG, GUIDED_REPS_TAG, TRACK_TAG];

type TaggableActivity = Pick<ActivityDefinition, 'automationLevel' | 'end_condition'>;

/**
 * The exercise's monitoring tag, or undefined when it should carry no chip at all.
 *
 * `eng._phase === 0` wins unconditionally: the library ships exercises that are phase 0 AND
 * carry a guided end condition (Y Balance, Hip Adduction Squeeze With Ball), and those stay
 * CAPTURE. Only phases at or above 1 are split by end condition.
 */
export function monitoringTagFor(
  activityDefinition?: TaggableActivity,
): MonitoringTag | undefined {
  const automationLevel = activityDefinition?.automationLevel;
  // `-1` is the library's "not released" sentinel; it has never had a chip.
  if (automationLevel === undefined || automationLevel === null) {
    return undefined;
  }
  if (automationLevel < 0) {
    return undefined;
  }
  if (automationLevel === 0) {
    return CAPTURE_TAG;
  }
  return GUIDED_REP_END_CONDITIONS.has(activityDefinition?.end_condition ?? '')
    ? GUIDED_REPS_TAG
    : TRACK_TAG;
}
